import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCw, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

const cell = "bg-obsidian-800 border-obsidian-600 rounded-none text-bone-100 h-9";
const btn = "rounded-none uppercase tracking-wider text-xs bg-transparent border border-bone-500 text-bone-100 hover:border-terracotta hover:text-terracotta hover:bg-transparent";

// Parent keys this by clip version + captions, so local edits survive unrelated refreshes.
export default function CaptionEditor({ captions, onSaved }) {
  const [rows, setRows] = useState(captions);
  const [busy, setBusy] = useState("");

  const edit = (i, k, v) => setRows((r) => r.map((row, j) => (j === i ? { ...row, [k]: k === "text" ? v : Number(v) } : row)));
  const call = async (kind, fn, ok) => {
    setBusy(kind);
    try {
      onSaved((await fn()).data);
      toast.success(ok);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update captions.");
    } finally {
      setBusy("");
    }
  };
  const save = () => call("save", () => api.put("/admin/founder-voice/captions", { captions: rows.filter((r) => r.text.trim()) }), "Captions saved.");
  const redo = () => call("redo", () => api.post("/admin/founder-voice/recaption"), "Re-transcribed with Whisper.");

  return (
    <div className="mb-8" data-testid="caption-editor">
      <p className="font-sans text-xs uppercase tracking-[0.2em] text-terracotta mb-3">Captions · timed by Whisper, edit any line</p>
      {rows.length === 0 && <p className="font-serif italic text-bone-500 mb-3">No captions yet.</p>}
      <div className="space-y-2 mb-4">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[70px_70px_1fr] gap-2" data-testid={`caption-row-${i}`}>
            <Input type="number" step="0.1" value={r.start} onChange={(e) => edit(i, "start", e.target.value)} className={cell} aria-label="Start seconds" />
            <Input type="number" step="0.1" value={r.end} onChange={(e) => edit(i, "end", e.target.value)} className={cell} aria-label="End seconds" />
            <Input value={r.text} onChange={(e) => edit(i, "text", e.target.value)} className={cell} data-testid={`caption-text-${i}`} aria-label="Caption text" />
          </div>
        ))}
      </div>
      <div className="flex gap-3 flex-wrap">
        <Button onClick={save} disabled={!!busy || !rows.length} data-testid="caption-save-btn" className={btn}>
          {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save captions
        </Button>
        <Button onClick={redo} disabled={!!busy} data-testid="caption-recaption-btn" className={btn}>
          {busy === "redo" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RotateCw className="h-4 w-4 mr-2" />} Re-transcribe
        </Button>
      </div>
    </div>
  );
}
